import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/Card';
import { Button } from '../../../components/ui/Button';
import { Badge } from '../../../components/ui/Badge';
import { Input } from '../../../components/ui/Input';
import { Textarea } from '../../../components/ui/Textarea';
import { Modal } from '../../../components/ui/Modal';
import {
  Settings, Save, RefreshCw, TestTube, AlertTriangle, CheckCircle, XCircle, Plus, Edit, Trash2, Eye, EyeOff, Brain, Zap, Target, MessageSquare, Lightbulb, Shield, Activity, BarChart3
} from 'lucide-react';
import MockDataService from '../../../services/MockDataService';

const AISettings = () => {
  const [settings, setSettings] = useState(null);
  const [aiModels, setAiModels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [selectedModel, setSelectedModel] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showTestModal, setShowTestModal] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    type: 'chat',
    provider: 'openai',
    apiKey: '',
    modelId: '',
    maxTokens: 1000,
    temperature: 0.7,
    enabled: true,
    description: ''
  });
  const [testResults, setTestResults] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const [settingsResponse, modelsResponse] = await Promise.all([
          MockDataService.getAISettings(),
          MockDataService.getAIModels()
        ]);

        if (settingsResponse.success) {
          setSettings(settingsResponse.data);
        }
        if (modelsResponse.success) {
          setAiModels(modelsResponse.data);
        }
      } catch (err) {
        setError("Failed to fetch AI settings data.");
        console.error("Error fetching AI settings:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const handleSaveSettings = async () => {
    try {
      setSaving(true);
      const response = await MockDataService.updateAISettings(settings);
      if (response.success) {
        setSettings(response.data);
        alert('AI settings saved successfully!');
      } else {
        alert('Failed to save AI settings');
      }
    } catch (err) {
      alert('Failed to save AI settings');
      console.error("Error saving settings:", err);
    } finally {
      setSaving(false);
    }
  };

  const handleCreateModel = async () => {
    try {
      setSaving(true);
      const response = await MockDataService.createAIModel(formData);
      if (response.success) {
        setAiModels(prev => [...prev, response.data]);
        setShowCreateModal(false);
        setFormData({
          name: '',
          type: 'chat',
          provider: 'openai',
          apiKey: '',
          modelId: '',
          maxTokens: 1000,
          temperature: 0.7,
          enabled: true,
          description: ''
        });
        alert('AI model created successfully!');
      } else {
        alert('Failed to create AI model');
      }
    } catch (err) {
      alert('Failed to create AI model');
      console.error("Error creating model:", err);
    } finally {
      setSaving(false);
    }
  };

  const handleEditModel = async () => {
    try {
      setSaving(true);
      const response = await MockDataService.updateAIModel(selectedModel.id, formData);
      if (response.success) {
        setAiModels(prev => prev.map(model => 
          model.id === selectedModel.id ? response.data : model
        ));
        setShowEditModal(false);
        setSelectedModel(null);
        alert('AI model updated successfully!');
      } else {
        alert('Failed to update AI model');
      }
    } catch (err) {
      alert('Failed to update AI model');
      console.error("Error updating model:", err);
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteModel = async (modelId) => {
    if (window.confirm('Are you sure you want to delete this AI model?')) {
      try {
        const response = await MockDataService.deleteAIModel(modelId);
        if (response.success) {
          setAiModels(prev => prev.filter(model => model.id !== modelId));
          alert('AI model deleted successfully!');
        } else {
          alert('Failed to delete AI model');
        }
      } catch (err) {
        alert('Failed to delete AI model');
        console.error("Error deleting model:", err);
      }
    }
  };

  const handleTestModel = async (model) => {
    try {
      setTesting(true);
      setSelectedModel(model);
      setShowTestModal(true);
      
      const response = await MockDataService.testAIModel(model.id);
      if (response.success) {
        setTestResults(response.data);
      } else {
        setTestResults({ success: false, message: response.message });
      }
    } catch (err) {
      setTestResults({ success: false, message: 'Test failed' });
      console.error("Error testing model:", err);
    } finally {
      setTesting(false);
    }
  };

  const handleToggleModel = async (modelId) => {
    try {
      const model = aiModels.find(m => m.id === modelId);
      const response = await MockDataService.updateAIModel(modelId, {
        ...model,
        enabled: !model.enabled
      });
      
      if (response.success) {
        setAiModels(prev => prev.map(model => 
          model.id === modelId ? response.data : model
        ));
      }
    } catch (err) {
      alert('Failed to toggle model status');
      console.error("Error toggling model:", err);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return 'success';
      case 'inactive': return 'secondary';
      case 'error': return 'destructive';
      case 'warning': return 'outline';
      default: return 'outline';
    }
  };

  const getTypeIcon = (type) => {
    switch (type) {
      case 'chat': return <MessageSquare className="h-4 w-4" />;
      case 'classification': return <Target className="h-4 w-4" />;
      case 'generation': return <Lightbulb className="h-4 w-4" />;
      case 'analysis': return <BarChart3 className="h-4 w-4" />;
      default: return <Brain className="h-4 w-4" />;
    }
  };

  if (loading) return <div className="p-6 text-center">Loading AI Settings...</div>;
  if (error) return <div className="p-6 text-center text-red-500">Error: {error}</div>;

  return (
    <div className="p-6 space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">AI Settings</h1>
          <p className="text-gray-600 dark:text-gray-400">Configure AI models and orchestration settings.</p>
        </div>
        <div className="flex space-x-2">
          <Button variant="outline" onClick={() => setShowCreateModal(true)}>
            <Plus className="h-4 w-4 mr-2" />
            New Model
          </Button>
          <Button onClick={handleSaveSettings} disabled={saving}>
            <Save className={`h-4 w-4 mr-2 ${saving ? 'animate-spin' : ''}`} />
            {saving ? 'Saving...' : 'Save Settings'}
          </Button>
        </div>
      </div>

      {/* Global Settings */}
      {settings && (
        <Card>
          <CardHeader>
            <CardTitle>Global AI Settings</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-2">Default Provider</label>
                <select
                  value={settings.defaultProvider}
                  onChange={(e) => setSettings({...settings, defaultProvider: e.target.value})}
                  className="w-full p-2 border rounded-md"
                >
                  <option value="openai">OpenAI</option>
                  <option value="anthropic">Anthropic</option>
                  <option value="google">Google</option>
                  <option value="azure">Azure OpenAI</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Default Temperature</label>
                <Input
                  type="number"
                  step="0.1"
                  min="0"
                  max="2"
                  value={settings.defaultTemperature}
                  onChange={(e) => setSettings({...settings, defaultTemperature: parseFloat(e.target.value)})}
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Max Tokens</label>
                <Input
                  type="number"
                  value={settings.maxTokens}
                  onChange={(e) => setSettings({...settings, maxTokens: parseInt(e.target.value)})}
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Request Timeout (seconds)</label>
                <Input
                  type="number"
                  value={settings.requestTimeout}
                  onChange={(e) => setSettings({...settings, requestTimeout: parseInt(e.target.value)})}
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Fallback Model</label>
              <select
                value={settings.fallbackModel}
                onChange={(e) => setSettings({...settings, fallbackModel: e.target.value})}
                className="w-full p-2 border rounded-md"
              >
                <option value="gpt-3.5-turbo">GPT-3.5 Turbo</option>
                <option value="gpt-4">GPT-4</option>
                <option value="claude-3-sonnet">Claude 3 Sonnet</option>
                <option value="gemini-pro">Gemini Pro</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Enable AI Orchestration</label>
              <select
                value={settings.enableOrchestration ? 'true' : 'false'}
                onChange={(e) => setSettings({...settings, enableOrchestration: e.target.value === 'true'})}
                className="w-full p-2 border rounded-md"
              >
                <option value="true">Enabled</option>
                <option value="false">Disabled</option>
              </select>
            </div>
          </CardContent>
        </Card>
      )}

      {/* AI Models List */}
      <Card>
        <CardHeader>
          <CardTitle>Configured AI Models ({aiModels.length})</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {aiModels.map((model) => (
              <div key={model.id} className="border rounded-lg p-4">
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <div className="flex items-center space-x-2 mb-2">
                      {getTypeIcon(model.type)}
                      <h3 className="text-lg font-semibold">{model.name}</h3>
                      <Badge variant={getStatusColor(model.status)}>
                        {model.status}
                      </Badge>
                      <Badge variant={model.enabled ? 'success' : 'secondary'}>
                        {model.enabled ? 'Enabled' : 'Disabled'}
                      </Badge>
                    </div>
                    <p className="text-gray-600 dark:text-gray-400 mb-2">{model.description}</p>
                    <div className="flex items-center space-x-4 text-sm text-gray-500">
                      <span className="flex items-center">
                        <Brain className="h-4 w-4 mr-1" />
                        {model.provider}
                      </span>
                      <span className="flex items-center">
                        <Activity className="h-4 w-4 mr-1" />
                        {model.type}
                      </span>
                      <span className="flex items-center">
                        <Shield className="h-4 w-4 mr-1" />
                        {model.securityLevel}
                      </span>
                    </div>
                  </div>
                  <div className="flex space-x-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleTestModel(model)}
                      disabled={testing}
                    >
                      <TestTube className="h-4 w-4 mr-1" />
                      Test
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        setSelectedModel(model);
                        setFormData(model);
                        setShowEditModal(true);
                      }}
                    >
                      <Edit className="h-4 w-4 mr-1" />
                      Edit
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleToggleModel(model.id)}
                    >
                      {model.enabled ? <EyeOff className="h-4 w-4 mr-1" /> : <Eye className="h-4 w-4 mr-1" />}
                      {model.enabled ? 'Disable' : 'Enable'}
                    </Button>
                    <Button
                      variant="destructive"
                      size="sm"
                      onClick={() => handleDeleteModel(model.id)}
                    >
                      <Trash2 className="h-4 w-4 mr-1" />
                      Delete
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Create Model Modal */}
      <Modal isOpen={showCreateModal} onClose={() => setShowCreateModal(false)}>
        <div className="p-6 max-w-2xl">
          <h2 className="text-xl font-bold mb-4">Create New AI Model</h2>
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-2">Name</label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  placeholder="Model name"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Type</label>
                <select
                  value={formData.type}
                  onChange={(e) => setFormData({...formData, type: e.target.value})}
                  className="w-full p-2 border rounded-md"
                >
                  <option value="chat">Chat</option>
                  <option value="classification">Classification</option>
                  <option value="generation">Generation</option>
                  <option value="analysis">Analysis</option>
                </select>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-2">Provider</label>
                <select
                  value={formData.provider}
                  onChange={(e) => setFormData({...formData, provider: e.target.value})}
                  className="w-full p-2 border rounded-md"
                >
                  <option value="openai">OpenAI</option>
                  <option value="anthropic">Anthropic</option>
                  <option value="google">Google</option>
                  <option value="azure">Azure OpenAI</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Model ID</label>
                <Input
                  value={formData.modelId}
                  onChange={(e) => setFormData({...formData, modelId: e.target.value})}
                  placeholder="gpt-3.5-turbo"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">API Key</label>
              <Input
                type="password"
                value={formData.apiKey}
                onChange={(e) => setFormData({...formData, apiKey: e.target.value})}
                placeholder="Your API key"
              />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-2">Max Tokens</label>
                <Input
                  type="number"
                  value={formData.maxTokens}
                  onChange={(e) => setFormData({...formData, maxTokens: parseInt(e.target.value)})}
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Temperature</label>
                <Input
                  type="number"
                  step="0.1"
                  min="0"
                  max="2"
                  value={formData.temperature}
                  onChange={(e) => setFormData({...formData, temperature: parseFloat(e.target.value)})}
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Description</label>
              <Textarea
                value={formData.description}
                onChange={(e) => setFormData({...formData, description: e.target.value})}
                placeholder="Model description"
                rows={3}
              />
            </div>
            <div className="flex items-center space-x-2">
              <input
                type="checkbox"
                id="enabled"
                checked={formData.enabled}
                onChange={(e) => setFormData({...formData, enabled: e.target.checked})}
              />
              <label htmlFor="enabled" className="text-sm font-medium">Enable model</label>
            </div>
            <div className="flex justify-end space-x-2">
              <Button variant="outline" onClick={() => setShowCreateModal(false)}>
                Cancel
              </Button>
              <Button onClick={handleCreateModel} disabled={saving}>
                <Plus className="h-4 w-4 mr-2" />
                {saving ? 'Creating...' : 'Create Model'}
              </Button>
            </div>
          </div>
        </div>
      </Modal>

      {/* Edit Model Modal */}
      <Modal isOpen={showEditModal} onClose={() => setShowEditModal(false)}>
        <div className="p-6 max-w-2xl">
          <h2 className="text-xl font-bold mb-4">Edit AI Model</h2>
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-2">Name</label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Type</label>
                <select
                  value={formData.type}
                  onChange={(e) => setFormData({...formData, type: e.target.value})}
                  className="w-full p-2 border rounded-md"
                >
                  <option value="chat">Chat</option>
                  <option value="classification">Classification</option>
                  <option value="generation">Generation</option>
                  <option value="analysis">Analysis</option>
                </select>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-2">Provider</label>
                <select
                  value={formData.provider}
                  onChange={(e) => setFormData({...formData, provider: e.target.value})}
                  className="w-full p-2 border rounded-md"
                >
                  <option value="openai">OpenAI</option>
                  <option value="anthropic">Anthropic</option>
                  <option value="google">Google</option>
                  <option value="azure">Azure OpenAI</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Model ID</label>
                <Input
                  value={formData.modelId}
                  onChange={(e) => setFormData({...formData, modelId: e.target.value})}
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">API Key</label>
              <Input
                type="password"
                value={formData.apiKey}
                onChange={(e) => setFormData({...formData, apiKey: e.target.value})}
              />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-2">Max Tokens</label>
                <Input
                  type="number"
                  value={formData.maxTokens}
                  onChange={(e) => setFormData({...formData, maxTokens: parseInt(e.target.value)})}
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Temperature</label>
                <Input
                  type="number"
                  step="0.1"
                  min="0"
                  max="2"
                  value={formData.temperature}
                  onChange={(e) => setFormData({...formData, temperature: parseFloat(e.target.value)})}
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Description</label>
              <Textarea
                value={formData.description}
                onChange={(e) => setFormData({...formData, description: e.target.value})}
                rows={3}
              />
            </div>
            <div className="flex items-center space-x-2">
              <input
                type="checkbox"
                id="editEnabled"
                checked={formData.enabled}
                onChange={(e) => setFormData({...formData, enabled: e.target.checked})}
              />
              <label htmlFor="editEnabled" className="text-sm font-medium">Enable model</label>
            </div>
            <div className="flex justify-end space-x-2">
              <Button variant="outline" onClick={() => setShowEditModal(false)}>
                Cancel
              </Button>
              <Button onClick={handleEditModel} disabled={saving}>
                <Save className="h-4 w-4 mr-2" />
                {saving ? 'Saving...' : 'Save Changes'}
              </Button>
            </div>
          </div>
        </div>
      </Modal>

      {/* Test Results Modal */}
      <Modal isOpen={showTestModal} onClose={() => setShowTestModal(false)}>
        <div className="p-6 max-w-lg">
          <h2 className="text-xl font-bold mb-4">Model Test Results</h2>
          {testResults && (
            <div className="space-y-4">
              <div className={`p-4 rounded-lg ${testResults.success ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'}`}>
                <div className="flex items-center space-x-2">
                  {testResults.success ? (
                    <CheckCircle className="h-5 w-5 text-green-600" />
                  ) : (
                    <XCircle className="h-5 w-5 text-red-600" />
                  )}
                  <span className={`font-medium ${testResults.success ? 'text-green-800' : 'text-red-800'}`}>
                    {testResults.success ? 'Test Passed' : 'Test Failed'}
                  </span>
                </div>
                <p className={`mt-2 ${testResults.success ? 'text-green-700' : 'text-red-700'}`}>
                  {testResults.message}
                </p>
                {testResults.details && (
                  <div className="mt-3">
                    <h4 className="font-medium text-sm">Details:</h4>
                    <pre className="text-xs bg-gray-100 p-2 rounded mt-1 overflow-auto">
                      {JSON.stringify(testResults.details, null, 2)}
                    </pre>
                  </div>
                )}
              </div>
              <div className="flex justify-end">
                <Button onClick={() => setShowTestModal(false)}>
                  Close
                </Button>
              </div>
            </div>
          )}
        </div>
      </Modal>
    </div>
  );
};

export default AISettings;

